/**
 * 时光漫游（design §11.11，P4b）的界面：屏幕中间略偏上的幻灯片卡片、底部一行状态和四个按钮的控制条、
 * 盖住画布的透明手势层。只管 DOM；路线、时间轴和镜头在 roam.ts / galaxy.ts，挑站、预取、状态字、键盘在 main.ts 接。
 */
import { el } from './dom.ts';

/** 一站要显示的东西；main.ts 按这一站的封面和回忆填好 */
export interface RoamSlide {
  /** 照片宽高比（已经夹在 0.56–1.8，同面板的 setAspect）：画框按它定形 */
  aspect: number;
  /** 画框的底色：主色渐变（压测、HEIC 解不开没有缩略图时就看见它，同面板的 swatch） */
  swatch: string;
  /** ThumbHash 的 dataURL：512 档到之前先垫着（小小的，放大自带模糊） */
  placeholder?: string;
  /** 已经取到的 512 档对象 URL：有了直接上清晰图 */
  image?: string;
  /** 「2024 年 5 月 20 日 · 星期一」（同面板，不带时刻） */
  date: string;
  /** 回忆标题；没有时是「在一起的第 N 天」/「认识之前」（同 describe） */
  title: string;
  /** 「地点 · N 张 · 由 某某 点亮」 */
  meta: string;
  /** 故事的第一行；没有是空串 */
  story: string;
  /** 这一站停几秒：照片慢慢推近走这么久（同 roam.ts 的停站时长） */
  hold: number;
}

export interface RoamUiHandlers {
  /** 点一下（画面或卡片）：暂停 / 继续 */
  onToggle(): void;
  /** 上一段 / 下一段（左右滑、按钮、← →） */
  onPrev(): void;
  onNext(): void;
  /** 退出漫游（按钮、Esc、T） */
  onExit(): void;
  /** 按住开始快进 ×2.5（true）、松手恢复 1（false） */
  onFast(on: boolean): void;
}

// 点一下、按住、左右滑的门槛
const EDGE = 20; // 离屏幕左右边缘这么远里按下不算：留给系统的返回手势（同 galaxy.ts 的 EDGE）
const TAP_PX = 6; // 点一下：移动不超过这么远
const TAP_MS = 350; //           按下不超过这么久（再久算按住快进）
const SWIPE_PX = 40; // 左右滑：水平超过这么远、而且比竖直的多
const FADE_MS = 450; // 卡片淡出 0.4 秒后从 DOM 收掉（略长于 CSS 的 .4s，让过渡走完）

/** 手势层和每张卡片共用同一套手势：点一下暂停、按住快进、左右滑换站 */
function gestures(node: HTMLElement, h: RoamUiHandlers): void {
  let down: { x: number; y: number; t: number; timer: number; fast: boolean } | null = null;
  node.addEventListener('pointerdown', (e) => {
    if (!e.isPrimary || down) return;
    if (e.pointerType !== 'mouse' && (e.clientX < EDGE || innerWidth - e.clientX < EDGE)) return;
    down = {
      x: e.clientX,
      y: e.clientY,
      t: performance.now(),
      fast: false,
      timer: window.setTimeout(() => {
        if (!down) return;
        down.fast = true;
        h.onFast(true);
      }, TAP_MS),
    };
    // 划走时手势跟着这根手指走；合成的 pointerdown 会抛错（自动化测试），包一层
    try {
      node.setPointerCapture(e.pointerId);
    } catch {
      // 没有真指针：手势丢了也不要紧
    }
  });
  node.addEventListener('pointermove', (e) => {
    if (!down || !e.isPrimary) return;
    // 动起来就不是按住：取消快进计时（已经开始的快进继续到松手）
    if (!down.fast && Math.hypot(e.clientX - down.x, e.clientY - down.y) > TAP_PX) clearTimeout(down.timer);
  });
  const finish = (e: PointerEvent, cancelled: boolean) => {
    if (!e.isPrimary) return;
    const d = down;
    down = null;
    if (!d) return;
    clearTimeout(d.timer);
    if (d.fast) return void h.onFast(false);
    if (cancelled) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    // 往左滑是下一段，往右滑是上一段
    if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) return void (dx < 0 ? h.onNext() : h.onPrev());
    if (Math.hypot(dx, dy) <= TAP_PX && performance.now() - d.t < TAP_MS) h.onToggle();
  };
  node.addEventListener('pointerup', (e) => finish(e, false));
  node.addEventListener('pointercancel', (e) => finish(e, true));
  // 按住是快进：别弹系统的长按菜单
  node.addEventListener('contextmenu', (e) => e.preventDefault());
}

export function mountRoamUi(parent: HTMLElement, h: RoamUiHandlers) {
  // 手势层在最底下（画布收不到按下），卡片在上，控制条最上
  const touch = el('div', { class: 'roam-touch', attrs: { 'aria-hidden': 'true' } });
  const cards = el('div', { class: 'roam-cards' });
  const line = el('p', { class: 'roam-line' });
  const progressI = el('span', { class: 'roam-progress-i' });
  const prev = el('button', { class: 'btn', text: '上一段', attrs: { type: 'button', title: '上一段（←）' }, on: { click: () => h.onPrev() } });
  const pause = el('button', { class: 'btn', text: '暂停', attrs: { type: 'button', title: '暂停 / 继续（空格）' }, on: { click: () => h.onToggle() } });
  const next = el('button', { class: 'btn', text: '下一段', attrs: { type: 'button', title: '下一段（→）' }, on: { click: () => h.onNext() } });
  const exit = el('button', { class: 'btn', text: '退出漫游', attrs: { type: 'button', title: '退出漫游（Esc）' }, on: { click: () => h.onExit() } });
  const bar = el(
    'div',
    { class: 'caption roam-bar', attrs: { 'aria-live': 'polite' } },
    line,
    el('span', { class: 'roam-progress', attrs: { 'aria-hidden': 'true' } }, progressI),
    el('div', { class: 'caption-row' }, prev, pause, next, exit),
  );
  const root = el('div', { class: 'roam' }, touch, cards, bar);
  root.hidden = true;
  parent.append(root);
  gestures(touch, h);

  /** 现在显示的这张卡的清晰图（512 档到了就换上）；正在淡出的前一张已经交给定时器收 */
  let current: HTMLImageElement | null = null;

  /** 现在还显示着的卡都开始淡出（到站换卡、离站、跳站都走这里） */
  const dismiss = () => {
    current = null;
    for (const node of [...cards.children]) {
      node.classList.remove('roam-in');
      setTimeout(() => node.remove(), FADE_MS);
    }
  };

  return {
    /** 到站：这一站的卡片淡入。上一张同时在淡出——跳站时两张交叉淡化 */
    arrive(v: RoamSlide) {
      dismiss();
      const ph = el('img', { class: 'roam-img roam-ph', attrs: { alt: '', 'aria-hidden': 'true', draggable: 'false' } });
      if (v.placeholder) ph.src = v.placeholder;
      else ph.hidden = true;
      const hd = el('img', { class: 'roam-img roam-hd', attrs: { alt: '', draggable: 'false' } });
      // 512 档在到站前就到了：直接上清晰图
      if (v.image) {
        hd.src = v.image;
        hd.classList.add('roam-hd-on');
      }
      const box = el('div', { class: 'roam-photo' }, el('div', { class: 'roam-zoom' }, ph, hd));
      box.style.background = v.swatch;
      box.style.setProperty('--roam-hold', `${v.hold}s`);
      const meta = el('p', { class: 'roam-meta', text: v.meta });
      meta.hidden = !v.meta;
      const story = el('p', { class: 'roam-story', text: v.story });
      story.hidden = !v.story;
      const card = el(
        'figure',
        { class: 'roam-card' },
        box,
        el('figcaption', { class: 'roam-body' }, el('p', { class: 'roam-date', text: v.date }), el('h2', { class: 'roam-title', text: v.title }), meta, story),
      );
      // 宽高比放在卡片上：卡片的宽度也要按它算（竖的照片卡片变窄），画框继承
      card.style.setProperty('--ar', String(v.aspect));
      gestures(card, h);
      current = hd;
      cards.append(card);
      // 等一帧再加 class，淡入过渡才起得来
      requestAnimationFrame(() => card.classList.add('roam-in'));
    },
    /** 这张的 512 档到了：淡入盖过 ThumbHash 模糊图（到站时还没到就一直带着模糊图，漫游不等网） */
    setImage(url: string) {
      const hd = current;
      if (!hd) return;
      hd.src = url;
      void hd.decode().then(
        () => {
          if (current === hd) hd.classList.add('roam-hd-on');
        },
        () => undefined,
      );
    },
    /** 离站：卡片淡出（0.4 秒） */
    depart() {
      dismiss();
    },
    /** 底部一行字：main.ts 按状态拼好（第几段、那天的日期、暂停中、快进中） */
    line(text: string) {
      line.textContent = text;
    },
    /** 每帧：整趟走了多少（0–1），进度细线用 transform 画 */
    progress(f: number) {
      progressI.style.transform = `scaleX(${Math.min(1, Math.max(0, f))})`;
    },
    /** 暂停中：按钮换成「继续」，照片的推近也跟着停住 */
    paused(on: boolean) {
      root.classList.toggle('roam-paused', on);
      pause.textContent = on ? '继续' : '暂停';
    },
    /** 漫游开始：手势层、控制条出来 */
    show() {
      root.hidden = false;
    },
    /** 漫游停了（走完、Esc、被打断）：卡片、手势层、控制条一起收起 */
    hide() {
      current = null;
      cards.replaceChildren();
      root.classList.remove('roam-paused');
      pause.textContent = '暂停';
      progressI.style.transform = 'scaleX(0)';
      root.hidden = true;
    },
    get open() {
      return !root.hidden;
    },
  };
}
